import React from "react";
import { Image as ImageIcon } from "lucide-react";
import { CHECKLIST_ITEMS, GENERAL_CHECKLIST_OPTIONS } from "./constants";

export const PDFPreview = ({ 
  images, 
  date, 
  description, 
  imagesPerPage, 
  pageCount, 
  zoom,
  checklistEnabled,
  checklist,
  maintenanceNotes,
  inspector,
  title
}) => {
  const getGridCols = (count) => {
    if (count <= 1) return 1;
    if (count <= 2) return 2;
    if (count <= 4) return 2;
    if (count <= 6) return 3;
    return Math.min(3, Math.ceil(Math.sqrt(count)));
  };

  const calculateImagesPerPage = () => {
    if (images.length === 0) return imagesPerPage;
    const calculatedPerPage = Math.ceil(images.length / pageCount);
    return Math.min(calculatedPerPage, imagesPerPage);
  };

  const pages = [];
  
  if (images.length > 0) {
    const imagesPerPageCalc = Math.ceil(images.length / pageCount);
    for (let i = 0; i < pageCount; i++) {
      const start = i * imagesPerPageCalc;
      const end = Math.min(start + imagesPerPageCalc, images.length);
      if (start < images.length) {
        pages.push(images.slice(start, end));
      } else {
        pages.push([]);
      }
    }
  } else {
    pages.push([]);
  }

  const totalPages = pages.length;

  return (
    <div className="space-y-8" data-testid="pdf-preview-container">
      {pages.map((pageImages, pageIndex) => {
        const pageEffectiveCount = pageImages.length > 0 ? pageImages.length : imagesPerPage;
        const pageCols = getGridCols(pageEffectiveCount);
        const isLastPage = pageIndex === totalPages - 1;
        
        return (
          <div
            key={pageIndex}
            className="a4-paper paper-shadow paper-scaled pdf-preview"
            style={{ transform: `scale(${zoom})` }}
            data-testid={`pdf-page-${pageIndex}`}
          >
            {/* Logo Banner - Left Aligned */}
            <div className="mb-4 flex justify-start" data-testid="logo-banner">
              <img
                src="https://tools.hvkurz.de/pictures/HV%20Scholze-KurzKurz%20Logobanner%202026%20Mail2.jpg"
                alt="Scholze-Kurz & Kurz Logo"
                className="h-auto max-h-24 object-contain"
                style={{ maxWidth: '60%' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div class="h-20 px-4 bg-slate-100 flex items-center text-sm text-muted-foreground border rounded">Scholze-Kurz & Kurz Immobilien GmbH</div>';
                }}
              />
            </div>

            {/* Internal Notice - Top Right */}
            <div className="absolute top-4 right-4 text-right" style={{ fontSize: '7pt', color: '#666666', fontStyle: 'italic', lineHeight: '1.5' }}>
              <div>Report nur zur internen Bearbeitung.</div>
              <div>Zustandsbeschreibungen ersetzen keine</div>
              <div>fachlichen Prüfung/Bewertungen.</div>
            </div>

            {/* Title */}
            <h1 className="title mb-2 mt-2" style={{ fontSize: '15pt', fontWeight: 'bold' }}>
              {title || "Dokumentation Objektbegehung"}
            </h1>

            {/* Date */}
            <p className="date-field mb-1" style={{ fontSize: '10pt' }}>
              Datum: {date || "_______________"}
            </p>

            {/* Description */}
            <p className="description-field mb-2" style={{ fontSize: '10pt' }}>
              {description || ""}
            </p>

            {/* Separator Line - only one if checklist disabled */}
            {(!checklistEnabled || pageIndex !== 0) && (
              <div className="w-full h-1 mb-3" style={{ backgroundColor: '#0097a7' }}></div>
            )}

            {/* Checklist Section - Gray Background */}
            {pageIndex === 0 && checklistEnabled && (
              <>
                <div className="w-full h-1 mb-3" style={{ backgroundColor: '#0097a7' }}></div>
                <div className="bg-gray-100 p-2 rounded mb-1" style={{ fontSize: '7pt' }}>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {CHECKLIST_ITEMS.map((item, idx) => {
                      const match = item.match(/^([^(]+)(\([^)]+\))?$/);
                      const mainText = match ? match[1].trim() : item;
                      const parenText = match && match[2] ? match[2] : '';
                      
                      const isFassade = item === "Gesamtansicht der Fassade";
                      const itemValue = checklist[item];
                      
                      const generalOption = GENERAL_CHECKLIST_OPTIONS.find(opt => opt.value === itemValue);
                      
                      const isChecked = isFassade 
                        ? (itemValue === true || (typeof itemValue === 'string' && itemValue && !generalOption))
                        : (itemValue === true || (typeof itemValue === 'string' && !generalOption));
                      
                      let statusText, statusColor, checkColor;
                      if (generalOption) {
                        const previewColorMap = {
                          gray: 'text-gray-600',
                          blue: 'text-blue-600',
                          green: 'text-green-600',
                          red: 'text-red-600',
                        };

                        statusText = generalOption.label;
                        statusColor = previewColorMap[generalOption.color] || 'text-green-600';
                        checkColor = statusColor;
                      } else if (isFassade && typeof itemValue === 'string' && itemValue) {
                        statusText = itemValue;
                        statusColor = 'text-green-600';
                        checkColor = 'text-green-600';
                      } else if (isChecked) {
                        statusText = 'GEPRÜFT/OK';
                        statusColor = 'text-green-600';
                        checkColor = 'text-green-600';
                      } else {
                        statusText = 'NACHPRÜFUNG!';
                        statusColor = 'text-orange-500';
                        checkColor = 'text-orange-500';
                      }
                      
                      return (
                        <div key={idx} className="flex items-start gap-0.5">
                          <span className={`font-semibold flex-shrink-0 ${checkColor}`}>
                            {(isChecked || generalOption) ? '✓' : '!'}
                          </span>
                          <div className="flex-1 flex flex-col">
                            <div className="flex justify-between items-start">
                              <span>{mainText}</span>
                              <span className={`font-semibold flex-shrink-0 ml-1 ${statusColor}`}>
                                {statusText}
                              </span>
                            </div>
                            {parenText && (
                              <span className="text-gray-500" style={{ fontSize: '5pt' }}>{parenText}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-gray-500 mt-2 italic" style={{ fontSize: '5pt', lineHeight: '1.3' }}>
                    Hinweis: Alle Angaben nur wenn vorhanden. Nichtvorhandenes unberücksichtigt. Es handelt sich um Sichtprüfungen die zur Begehung zugänglich und erkennbar waren.<br/>
                    Die Angaben dienen nur zur internen Besprechung und/oder zur Kontrolle/QS der beauftragten Fachfirmen - ohne Gewähr.
                  </p>
                </div>
                <div className="w-full h-1 mb-3" style={{ backgroundColor: '#0097a7' }}></div>
              </>
            )}

            {/* Image Grid */}
            <div
              className={`image-grid cols-${pageCols}`}
              style={{
                gridTemplateColumns: `repeat(${pageCols}, 1fr)`,
                gap: '3mm',
              }}
            >
              {pageImages.map((image, idx) => (
                <div key={image.id} className="image-cell">
                  <div 
                    className="relative bg-gray-100 border border-gray-200"
                    style={{ 
                      aspectRatio: '4/3',
                      minHeight: pageImages.length === 1 ? '120px' : '80px'
                    }}
                  >
                    <img
                      src={image.data}
                      alt={image.caption || `Bild ${idx + 1}`}
                      className="w-full h-full object-cover"
                      style={{ transform: `rotate(${image.rotation}deg)` }}
                    />
                  </div>
                  <p className="caption-text mt-0.5" style={{ fontSize: '8pt' }}>
                    {image.caption || ""}
                  </p>
                </div>
              ))}
              {pageImages.length === 0 &&
                Array.from({ length: Math.min(imagesPerPage, 4) }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="image-cell">
                    <div className="relative aspect-[4/3] bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-gray-300" />
                    </div>
                  </div>
                ))}
            </div>

            {/* Separator Line after Images */}
            <div className="w-full h-1 mt-3 mb-2" style={{ backgroundColor: '#0097a7' }}></div>

            {/* Last Page: Maintenance Notes & Inspector */}
            {isLastPage && (
              <div className="mt-3 space-y-2">
                {maintenanceNotes && (
                  <div>
                    <p className="font-semibold" style={{ fontSize: '9pt' }}>
                      Laufende oder abgeschlossene Instandhaltungs- und Instandsetzungsmaßnahmen, Priorisierung:
                    </p>
                    <p style={{ fontSize: '9pt' }}>{maintenanceNotes}</p>
                  </div>
                )}
                {inspector && (
                  <div className={maintenanceNotes ? "pt-3" : "pt-1"}>
                    <p style={{ fontSize: '9pt' }}>
                      <span className="font-semibold">Prüfer:</span> {inspector}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="paper-footer">
              <span>Scholze-Kurz & Kurz Immobilien GmbH • www.hvkurz.de</span>
              <span>Seite {pageIndex + 1} von {totalPages}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PDFPreview;
